import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { App } from '@df/core/app';
import { Resource } from '@df/catalog/resource';
import { IRequestCallback } from '@df/api/request/request.service';
import { Review } from '@df/catalog/review/review';
import defaultsDeep from 'lodash-es/defaultsDeep';

export interface ILoadReviewsRequestParams {
    verbosity: number;
    limit?: number;
    skip?: number;
    sort?: object;
    store?: number;
    filter?: { [key: string]: any };
}

export enum ReviewType {
    /**
     * Product review type id
     */
    product = 1,
    /**
     * Merchant review type id
     */
    merchant = 4,
    hub = 16
}

declare module '@df/catalog/resource' {
    interface IResources {
        review: ReviewResource;
    }
}
@Injectable({
    providedIn: 'root'
})
export class ReviewResource extends Resource<Review> {
    static override instance = Review;

    readonly invalidatesUniversal = false;
    readonly invalidatesPrerender = false;

    /**
     * Loads all reviews existing in db
     */
    loadMany(request: ILoadReviewsRequestParams): Promise<Review[]> {
        return this.find(
            defaultsDeep(request, {
                filter: {},
                sort: {
                    date: -1
                },
                limit: 0,
                skip: 0
            })
        );
    }

    /**
     * Loads all merchant reviews existing in db
     */
    loadForMerchant(request: ILoadReviewsRequestParams): Promise<Review[]> {
        return this.loadMany(defaultsDeep(request, { filter: { type_id: ReviewType.merchant } }));
    }

    /**
     * Loads all product reviews existing in db
     */
    loadForProduct(request: ILoadReviewsRequestParams): Promise<Review[]> {
        return this.loadMany(defaultsDeep(request, { filter: { type_id: ReviewType.product } }));
    }

    /**
     * Loads all reviews for products
     */
    loadByProduct(productIds: number[], request: ILoadReviewsRequestParams) {
        return this.loadMany(
            defaultsDeep(request, {
                filter: {
                    type_id: ReviewType.product,
                    entity_id: {
                        $in: productIds
                    }
                }
            })
        );
    }

    loadForHub(hubIds: number[], request: ILoadReviewsRequestParams): Promise<Review[]> {
        return this.loadMany(
            defaultsDeep(request, {
                filter: { type_id: ReviewType.hub, entity_id: { $in: hubIds } }
            })
        );
    }
}
